/**
 * Voice notes (web-plan.md D55). The desktop records through libopus inside
 * `echo_core::media::voice_note`; a browser cannot carry that C, so the
 * division of labour moves — and the *artifacts* stay identical:
 *
 * - the browser's own Opus encoder (WebCodecs `AudioEncoder`) produces the
 *   packets — `MediaRecorder` will not do, Safari does not write ogg (D55),
 *   and WebCodecs gives us raw packets to put into OUR container;
 * - the Ogg Opus file is written by `echo_core::media::ogg` through wasm —
 *   one muxer on every client, byte-compatible on the wire;
 * - the 100×5-bit waveform comes from `echo_wire::waveform` over the same
 *   PCM the encoder ate — the same builder the desktop runs.
 *
 * The 10-minute cap is client-declared (D129) and enforced here exactly as
 * the desktop enforces it.
 */
import { currentLifetime, isCurrent } from "./lifetime";
import { ogg_opus_mux, waveform_from_pcm } from "../wasm/chat/echo_chat_wasm";

export const MAX_DURATION_MS = 10 * 60 * 1000;
const SAMPLE_RATE = 48_000;
const FRAME_SAMPLES = 960; // 20 ms at 48 kHz — the frame the waveform builder expects
const BITRATE = 24_000; // the desktop's voice-note bitrate
/** libopus reports 6.5 ms lookahead for our configurations; the browser's
 * encoder differs, but pre-skip only trims the primed samples — 312 is the
 * value every EchoFloat file declares (ogg.rs DEFAULT_PRE_SKIP). */
const PRE_SKIP = 312;

export interface Recording {
  /** The finished .ogg bytes. */
  ogg: Uint8Array;
  /** The packed waveform, base64 (standard alphabet, padded). */
  waveformBase64: string;
  durationMs: number;
}

export function recorderSupported(): boolean {
  return typeof AudioEncoder !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
}

export class VoiceRecorder {
  private readonly lifetime = currentLifetime();
  private cancelled = false;
  private stopped = false;
  private readonly onSessionEnd = () => this.cancel();

  constructor() {
    this.lifetime.signal.addEventListener("abort", this.onSessionEnd, { once: true });
  }

  private checkActive(): void {
    if (this.cancelled || this.stopped || !isCurrent(this.lifetime)) {
      throw new DOMException("recording cancelled", "AbortError");
    }
  }

  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private encoder: AudioEncoder | null = null;
  private node: AudioWorkletNode | null = null;
  private packets: Uint8Array[] = [];
  private packetSamples: number[] = [];
  private pcm: Int16Array[] = [];
  private startedAt = 0;
  private level = 0;
  private onLimit: (() => void) | null = null;

  /** 0..=1 for the meter. */
  currentLevel(): number {
    return this.level;
  }

  elapsedMs(): number {
    return this.startedAt === 0 ? 0 : Date.now() - this.startedAt;
  }

  async start(onLimit: () => void): Promise<void> {
    this.checkActive();
    this.onLimit = onLimit;
    // The browser's own APM (D54): the same AEC/NS/AGC family the native
    // client runs, for free.
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    if (this.cancelled || this.stopped || !isCurrent(this.lifetime)) {
      stream.getTracks().forEach((track) => track.stop());
      this.checkActive();
    }
    this.stream = stream;
    this.ctx = new AudioContext({ sampleRate: SAMPLE_RATE });
    await this.ctx.audioWorklet.addModule("/pcm-worklet.js");
    this.checkActive();
    const source = this.ctx.createMediaStreamSource(this.stream);
    this.node = new AudioWorkletNode(this.ctx, "pcm-capture", {
      numberOfInputs: 1,
      numberOfOutputs: 0,
    });

    this.encoder = new AudioEncoder({
      output: (chunk) => {
        if (this.cancelled || !isCurrent(this.lifetime)) return;
        const buf = new Uint8Array(chunk.byteLength);
        chunk.copyTo(buf);
        this.packets.push(buf);
        // WebCodecs reports µs; the granule wants 48 kHz samples.
        this.packetSamples.push(Math.round(((chunk.duration ?? 20_000) * 48) / 1000));
      },
      error: () => {
        // A dead encoder ends the recording at whatever length it reached.
      },
    });
    this.encoder.configure({
      codec: "opus",
      sampleRate: SAMPLE_RATE,
      numberOfChannels: 1,
      bitrate: BITRATE,
    });

    this.node.port.onmessage = (e: MessageEvent<Float32Array>) => {
      if (this.cancelled || !isCurrent(this.lifetime)) return;
      // A copy pinned to a plain ArrayBuffer: AudioData refuses the
      // SharedArrayBuffer-typed view TS infers for a transferred buffer.
      const f32 = new Float32Array(e.data);
      // f32 → i16 for the waveform (the builder's scale), and AudioData for
      // the encoder.
      const i16 = new Int16Array(f32.length);
      let peak = 0;
      for (let i = 0; i < f32.length; i += 1) {
        const v = Math.max(-1, Math.min(1, f32[i]!));
        i16[i] = (v * 0x7fff) | 0;
        const abs = Math.abs(v);
        if (abs > peak) peak = abs;
      }
      this.level = peak;
      this.pcm.push(i16);
      if (this.encoder && this.encoder.state === "configured") {
        const data = new AudioData({
          format: "f32",
          sampleRate: SAMPLE_RATE,
          numberOfFrames: f32.length,
          numberOfChannels: 1,
          timestamp: 0,
          data: f32,
        });
        this.encoder.encode(data);
        data.close();
      }
      if (this.elapsedMs() >= MAX_DURATION_MS) this.onLimit?.();
    };

    source.connect(this.node);
    this.startedAt = Date.now();
  }

  async stop(): Promise<Recording | null> {
    this.stopped = true;
    const durationMs = this.elapsedMs();
    if (this.encoder && this.encoder.state === "configured") {
      await this.encoder.flush().catch(() => {});
    }
    if (this.cancelled || !isCurrent(this.lifetime)) return null;
    this.teardown(false);
    if (this.packets.length === 0) return null;

    const total = this.packets.reduce((n, p) => n + p.length, 0);
    const flat = new Uint8Array(total);
    const lens = new Uint32Array(this.packets.length);
    const samples = new Uint32Array(this.packetSamples.length);
    let at = 0;
    for (let i = 0; i < this.packets.length; i += 1) {
      flat.set(this.packets[i]!, at);
      at += this.packets[i]!.length;
      lens[i] = this.packets[i]!.length;
      samples[i] = this.packetSamples[i]!;
    }
    const ogg = ogg_opus_mux(flat, lens, samples, PRE_SKIP);

    const pcmTotal = this.pcm.reduce((n, p) => n + p.length, 0);
    const pcmFlat = new Int16Array(pcmTotal);
    at = 0;
    for (const frame of this.pcm) {
      pcmFlat.set(frame, at);
      at += frame.length;
    }
    const packed = waveform_from_pcm(pcmFlat, FRAME_SAMPLES);
    let bin = "";
    for (const b of packed) bin += String.fromCharCode(b);

    this.packets = [];
    this.packetSamples = [];
    this.pcm = [];
    return { ogg, waveformBase64: btoa(bin), durationMs };
  }

  cancel(): void {
    this.cancelled = true;
    this.teardown();
  }

  private teardown(clear = true): void {
    this.lifetime.signal.removeEventListener("abort", this.onSessionEnd);
    this.onLimit = null;
    this.node?.port.close();
    this.node?.disconnect();
    this.node = null;
    if (this.encoder && this.encoder.state !== "closed") this.encoder.close();
    this.encoder = null;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    void this.ctx?.close();
    this.ctx = null;
    if (clear) {
      this.packets = [];
      this.packetSamples = [];
      this.pcm = [];
    }
    this.startedAt = 0;
  }
}
